/**
 * Facturación Masiva Mensual (v3.9.0).
 *
 * Flujo: 1) configuración → 2) borrador + Informe de Control (impedimentos / advertencias)
 * → 3) resultado con los comprobantes emitidos y los rechazos de ARCA.
 *
 * El comprobante (Factura o Nota de Débito), el talonario y la factura de origen los
 * deriva el backend a partir del esquema del servicio y la ficha del residente.
 */
import { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { fmtDate } from '../lib/datetime';
import { Sparkles, Loader2, AlertTriangle, ArrowLeft, CheckCircle2, FileSpreadsheet, FileText, ShieldAlert, XCircle, Printer } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function currentMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function lastDayOfMonth(yyyymm) {
  const [y, m] = yyyymm.split('-').map(Number);
  return new Date(y, m, 0).toISOString().slice(0, 10);
}

function ControlBlock({ block, kind }) {
  const [open, setOpen] = useState(kind === 'E');
  const tone = kind === 'E'
    ? 'border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-900'
    : 'border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800';
  const text = kind === 'E' ? 'text-rose-900 dark:text-rose-200' : 'text-amber-900 dark:text-amber-200';
  const INCLUSION = {
    incluido: { label: 'Se factura en esta corrida', cls: 'border-emerald-400 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-300' },
    excluido: { label: 'No se factura · queda afuera', cls: 'border-slate-400 text-slate-600 bg-slate-100 dark:bg-slate-800/60 dark:text-slate-300' },
    bloquea: { label: 'Frena la emisión · corregir', cls: 'border-rose-400 text-rose-700 bg-rose-100 dark:bg-rose-950/40 dark:text-rose-300' },
  };
  const inc = INCLUSION[block.inclusion] || INCLUSION.incluido;
  return (
    <div className={`rounded-md border ${tone} p-2`} data-testid={`control-block-${block.code}`}>
      <button type="button" onClick={() => setOpen(o => !o)} className={`w-full text-left text-sm flex items-center gap-2 font-medium ${text}`}>
        {kind === 'E' ? <ShieldAlert className="w-4 h-4 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
        <span className="font-mono font-bold">{block.code}</span>
        <span className="flex-1">{block.title}</span>
        <Badge variant="outline" className={`text-[10px] whitespace-nowrap ${inc.cls}`} data-testid={`control-inclusion-${block.code}`}>{inc.label}</Badge>
        <Badge variant="outline" className="text-[10px]">{block.count} caso{block.count === 1 ? '' : 's'}</Badge>
      </button>
      {open && (
        <>
          <p className="text-[11px] mt-1 opacity-80">{block.hint}</p>
          <Table className="mt-2">
            <TableHeader><TableRow>
              <TableHead className="text-xs">Residente</TableHead>
              <TableHead className="text-xs">DNI</TableHead>
              <TableHead className="text-xs">Concepto</TableHead>
              <TableHead className="text-xs">Detalle</TableHead>
              <TableHead className="text-xs">Qué hacer</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {block.rows.map((r, i) => (
                <TableRow key={i} data-testid={`control-row-${block.code}-${i}`}>
                  <TableCell className="text-xs">{r.resident_name || '—'}</TableCell>
                  <TableCell className="text-xs font-mono">{r.dni || '—'}</TableCell>
                  <TableCell className="text-xs">{r.service_name || '—'}</TableCell>
                  <TableCell className="text-xs">{r.detail}</TableCell>
                  <TableCell className="text-xs opacity-80">{r.action}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
}

export function MassBillingDialog({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1);
  const [period, setPeriod] = useState(currentMonthISO());
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [draftData, setDraftData] = useState(null);
  const [selections, setSelections] = useState({});
  const [result, setResult] = useState(null);
  const [services, setServices] = useState([]);
  const [serviceIds, setServiceIds] = useState([]);
  const [mergeConcepts, setMergeConcepts] = useState(false);
  const [lineMode, setLineMode] = useState('');

  const reset = () => {
    setStep(1); setDraftData(null); setSelections({}); setResult(null);
    setPeriod(currentMonthISO()); setIssueDate(''); setDueDate(''); setNotes('');
    setServiceIds([]); setMergeConcepts(false); setLineMode('');
  };

  useEffect(() => {
    if (!open) return;
    axios.get(`${BACKEND_URL}/api/resident-services`, { withCredentials: true })
      .then(r => setServices((r.data || []).filter(x => (x.billing_type || 'abono') === 'abono' && x.is_active !== false)))
      .catch(() => setServices([]));
  }, [open]);

  const toggleService = (sid) => setServiceIds(prev =>
    prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);

  const onClose = (o) => {
    if (!o) { reset(); onOpenChange(false); }
    else onOpenChange(true);
  };

  const reqBody = () => ({
    period, issue_date: issueDate, due_date: dueDate || null,
    service_ids: serviceIds.length ? serviceIds : null,
    merge_concepts: mergeConcepts,
    line_mode: lineMode || null,
  });

  const generate = async (opts = {}) => {
    const body = { ...reqBody(), ...opts };
    if (!period) { toast.error('Indicá el período (YYYY-MM)'); return; }
    if (!issueDate) { toast.error('Indicá la fecha de emisión'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/billing/run/preview`, body, { withCredentials: true });
      setDraftData(r.data);
      const sel = {};
      (r.data.drafts || []).forEach((g) => { g.lines.forEach((_, i) => { sel[`${g.group_key}|${i}`] = true; }); });
      setSelections(sel);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar borrador');
    } finally { setLoading(false); }
  };

  const rebuild = (patch) => {
    if (patch.merge_concepts !== undefined) setMergeConcepts(patch.merge_concepts);
    if (patch.line_mode !== undefined) setLineMode(patch.line_mode);
    generate(patch);
  };

  const downloadControl = async () => {
    try {
      const r = await axios.post(`${BACKEND_URL}/api/billing/run/control.pdf`, reqBody(),
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `control_corrida_${period}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('No se pudo generar el informe'); }
  };

  const lineKey = (gKey, idx) => `${gKey}|${idx}`;
  const toggleLine = (gKey, idx) => setSelections(s => ({ ...s, [lineKey(gKey, idx)]: !s[lineKey(gKey, idx)] }));
  const toggleGroup = (g, value) => setSelections(s => {
    const out = { ...s };
    g.lines.forEach((_, i) => { out[lineKey(g.group_key, i)] = value; });
    return out;
  });
  const toggleAll = (value) => setSelections(() => {
    if (!draftData) return {};
    const out = {};
    draftData.drafts.forEach((g) => { g.lines.forEach((_, i) => { out[lineKey(g.group_key, i)] = value; }); });
    return out;
  });
  const groupSelectedCount = (g) => g.lines.filter((_, i) => selections[lineKey(g.group_key, i)]).length;
  const totalLinesCount = useMemo(() => {
    if (!draftData) return 0;
    return draftData.drafts.reduce((s, g) => s + g.lines.length, 0);
  }, [draftData]);
  const totalSelectedCount = useMemo(() => {
    if (!draftData) return 0;
    return draftData.drafts.reduce((s, g) => s + groupSelectedCount(g), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftData, selections]);

  const summary = useMemo(() => {
    if (!draftData) return { groups: 0, lines: 0, total: 0, fc: 0, nd: 0, arca: 0 };
    let groups = 0, lines = 0, total = 0, fc = 0, nd = 0, arca = 0;
    draftData.drafts.forEach((g) => {
      const selLines = g.lines.filter((_, i) => selections[lineKey(g.group_key, i)]);
      if (selLines.length > 0) {
        groups += 1; lines += selLines.length;
        total += selLines.reduce((s, ln) => s + (ln.total || 0), 0);
        if (g.doc_type === 'ND') nd += 1; else fc += 1;
        if (g.talonario === 'arca') arca += 1;
      }
    });
    return { groups, lines, total: Math.round(total * 100) / 100, fc, nd, arca };
  }, [draftData, selections]);

  const blocked = !!draftData?.control?.blocked;

  const execute = async () => {
    if (!draftData) return;
    if (blocked) { toast.error('Resolvé los impedimentos del Informe de Control antes de emitir'); return; }
    if (summary.groups === 0) { toast.error('No hay líneas seleccionadas'); return; }
    setLoading(true);
    try {
      const drafts = draftData.drafts.map((g) => ({
        ...g,
        lines: g.lines.map((ln, i) => ({ ...ln, selected: !!selections[lineKey(g.group_key, i)] })),
      })).filter(g => g.lines.some(l => l.selected));

      const r = await axios.post(`${BACKEND_URL}/api/billing/run/execute`, {
        period, issue_date: issueDate, due_date: dueDate || null, notes: notes || null, drafts,
      }, { withCredentials: true });
      setResult(r.data);
      setStep(3);
      if (r.data.rejected?.length) {
        toast.warning(`${r.data.created.length} emitidos · ${r.data.rejected.length} rechazados por ARCA`);
      } else {
        toast.success(`${r.data.created.length} comprobantes emitidos`);
      }
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al emitir');
    } finally { setLoading(false); }
  };

  const downloadRun = async (ext) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/billing/runs/${result.billing_run_id}/summary.${ext}`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `corrida_${period}_${result.billing_run_id.slice(-6)}.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('No se pudo descargar el resumen'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="mass-billing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            Facturación masiva mensual {step === 1 ? '· Configuración' : step === 2 ? '· Borrador e Informe de Control' : '· Resultado'}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              La corrida emite los servicios de <b>abono</b> encendidos en cada ficha: <b>facturas</b> a las obras
              sociales y a los residentes particulares, y <b>notas de débito</b> de los adicionales asociadas a la
              factura de origen. El comprobante y el talonario los define el esquema de cada servicio.
              Si el residente ingresó dentro del período, se prorratea su primer mes.
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Período (YYYY-MM) *</Label>
                <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="mb-period" />
              </div>
              <div>
                <Label className="text-xs">Fecha de emisión *</Label>
                <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} data-testid="mb-issue-date" />
                {!issueDate && period && (
                  <button type="button" className="text-[11px] text-blue-600 hover:underline mt-1"
                          onClick={() => setIssueDate(lastDayOfMonth(period))}>
                    Usar último día del mes
                  </button>
                )}
              </div>
              <div>
                <Label className="text-xs">Fecha de vencimiento</Label>
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="mb-due-date" />
                <p className="text-[11px] text-muted-foreground mt-1">Vacío = emisión + 7 días</p>
              </div>
            </div>
            {period && (
              <div className="rounded-md border bg-blue-50/60 dark:bg-blue-950/30 px-3 py-2 text-xs text-blue-900 dark:text-blue-200" data-testid="mb-period-info">
                <b>Período del servicio (ARCA):</b> {fmtDate(`${period}-01`)} al {fmtDate(lastDayOfMonth(period))}.{' '}
                El Vto. de pago se calcula como emisión + 7 días y, si quedara antes del fin del período, se ajusta a fin de período + 7.
              </div>
            )}
            <div className="rounded-md border p-3" data-testid="mb-service-filter">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Abonos a facturar</Label>
                <div className="flex gap-2">
                  <button type="button" className="text-[11px] text-blue-600 hover:underline"
                          onClick={() => setServiceIds(services.map(x => x.service_id))} data-testid="mb-select-all-services">
                    Seleccionar todos
                  </button>
                  <button type="button" className="text-[11px] text-blue-600 hover:underline"
                          onClick={() => setServiceIds([])} data-testid="mb-clear-services">
                    Limpiar
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
                {services.map(svc => (
                  <label key={svc.service_id} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
                    <input type="checkbox" className="h-3.5 w-3.5" checked={serviceIds.includes(svc.service_id)}
                           onChange={() => toggleService(svc.service_id)}
                           data-testid={`mb-service-${svc.service_id}`} />
                    <span>{svc.name}</span>
                    <Badge variant="outline" className="text-[9px]">{svc.emission_mode === 'nota_debito' ? 'ND' : 'FC'}</Badge>
                  </label>
                ))}
                {services.length === 0 && <span className="text-xs text-muted-foreground">Sin abonos en el catálogo</span>}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                {serviceIds.length === 0
                  ? 'Sin selección = se facturan TODOS los abonos activos.'
                  : `Se facturarán solo los ${serviceIds.length} abono(s) marcado(s).`}
              </p>
            </div>
            <div>
              <Label className="text-xs">Notas internas (opcional)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej: Corrida mensual junio 2026" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onClose(false)}>Cancelar</Button>
              <Button onClick={() => generate()} disabled={loading} data-testid="mb-generate-btn">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Generar borrador
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && draftData && (
          <div className="space-y-3">
            {/* Semáforo del Informe de Control */}
            <div className={`rounded-md border p-3 flex items-center justify-between gap-3 flex-wrap ${blocked
              ? 'border-rose-300 bg-rose-50 dark:bg-rose-950/30'
              : 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30'}`} data-testid="mb-control-banner">
              <div className="flex items-center gap-3">
                {blocked ? <ShieldAlert className="w-6 h-6 text-rose-600" /> : <CheckCircle2 className="w-6 h-6 text-emerald-600" />}
                <div>
                  <p className={`font-semibold text-sm ${blocked ? 'text-rose-800 dark:text-rose-200' : 'text-emerald-800 dark:text-emerald-200'}`}>
                    {blocked ? 'Emisión bloqueada · hay impedimentos que resolver' : 'Control OK · listo para emitir'}
                  </p>
                  <p className="text-xs opacity-80" data-testid="mb-control-counts">
                    <span className="text-rose-700 dark:text-rose-300 font-semibold">{draftData.control.impediments_count} impedimento(s)</span>
                    {' · '}
                    <span className="text-amber-700 dark:text-amber-300 font-semibold">{draftData.control.warnings_count} advertencia(s)</span>
                    {' · '}
                    {summary.groups} comprobante(s) listos
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={downloadControl} data-testid="mb-control-pdf">
                <Printer className="w-4 h-4 mr-1.5" />Imprimir informe de control
              </Button>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <Card><CardContent className="pt-4">
                <div className="text-[11px] text-muted-foreground">Facturas</div>
                <div className="text-2xl font-bold" data-testid="mb-summary-fc">{summary.fc}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-[11px] text-muted-foreground">Notas de débito</div>
                <div className="text-2xl font-bold text-violet-700" data-testid="mb-summary-nd">{summary.nd}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-[11px] text-muted-foreground">En ARCA (con CAE)</div>
                <div className="text-2xl font-bold text-indigo-700" data-testid="mb-summary-arca">{summary.arca}</div>
                <div className="text-[10px] text-muted-foreground">{draftData.control.arca?.message}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-[11px] text-muted-foreground">Total a facturar</div>
                <div className="text-2xl font-bold text-emerald-700" data-testid="mb-summary-total">{fmt(summary.total)}</div>
                <div className="text-[10px] text-muted-foreground">{summary.lines} renglones</div>
              </CardContent></Card>
            </div>

            {draftData.control.impediments.length > 0 && (
              <div className="space-y-2" data-testid="mb-impediments">
                <p className="text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">Impedimentos — bloquean la emisión</p>
                {draftData.control.impediments.map(b => <ControlBlock key={b.code} block={b} kind="E" />)}
              </div>
            )}

            {(draftData.invoice_format?.ask_merge || []).length > 0 && (
              <div className="rounded-md border border-teal-300 bg-teal-50 dark:bg-teal-950/30 p-3 space-y-2"
                   data-testid="mb-merge-question">
                <p className="text-sm font-medium text-teal-900 dark:text-teal-200">
                  {draftData.invoice_format.ask_merge.map(a =>
                    `${a.insurer_name} tiene ${a.services.length} conceptos en esta corrida (${a.services.join(', ')})`
                  ).join(' · ')}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs">¿Los pongo en la misma factura?</span>
                  <Button size="sm" variant={mergeConcepts ? 'default' : 'outline'}
                          onClick={() => rebuild({ merge_concepts: true })}
                          data-testid="mb-merge-yes">Sí, una sola factura</Button>
                  <Button size="sm" variant={!mergeConcepts ? 'default' : 'outline'}
                          onClick={() => rebuild({ merge_concepts: false })}
                          data-testid="mb-merge-no">No, una por concepto</Button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs">¿Cómo sale el renglón?</span>
                  <Button size="sm" variant={draftData.invoice_format.line_mode === 'summary' ? 'default' : 'outline'}
                          onClick={() => rebuild({ line_mode: 'summary' })}
                          data-testid="mb-mode-summary">Resumido (uno por concepto)</Button>
                  <Button size="sm" variant={draftData.invoice_format.line_mode === 'detail' ? 'default' : 'outline'}
                          onClick={() => rebuild({ line_mode: 'detail' })}
                          data-testid="mb-mode-detail">Detallado (uno por afiliado)</Button>
                  <Button size="sm" variant={!draftData.invoice_format.line_mode ? 'default' : 'outline'}
                          onClick={() => rebuild({ line_mode: null })}
                          data-testid="mb-mode-auto">Como esté configurado</Button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 flex-wrap border-b pb-2" data-testid="mb-bulk-select-bar">
              <div className="text-xs text-muted-foreground">
                <b className="text-slate-900 dark:text-slate-100" data-testid="mb-bulk-count">{totalSelectedCount}</b> / {totalLinesCount} renglones seleccionados
                {draftData.drafts.length > 0 && <span className="ml-1">· {draftData.drafts.length} comprobantes</span>}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => toggleAll(true)}
                        disabled={totalSelectedCount === totalLinesCount}
                        data-testid="mb-bulk-select-all">Tildar todos</Button>
                <Button size="sm" variant="outline" onClick={() => toggleAll(false)}
                        disabled={totalSelectedCount === 0}
                        data-testid="mb-bulk-deselect-all">Destildar todos</Button>
              </div>
            </div>

            <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
              {draftData.drafts.map((g) => {
                const selCount = groupSelectedCount(g);
                const allOn = selCount === g.lines.length;
                return (
                  <Card key={g.group_key} data-testid={`mb-draft-${g.group_key}`}>
                    <CardContent className="pt-3 pb-3">
                      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="checkbox"
                            checked={allOn}
                            ref={(el) => { if (el) el.indeterminate = selCount > 0 && !allOn; }}
                            onChange={(e) => toggleGroup(g, e.target.checked)}
                            data-testid={`mb-group-toggle-${g.group_key}`}
                          />
                          <Badge className={`text-[10px] ${g.doc_type === 'ND' ? 'bg-violet-100 text-violet-800' : 'bg-blue-100 text-blue-800'}`}>
                            {g.doc_type === 'ND' ? 'NOTA DE DÉBITO' : 'FACTURA'}
                          </Badge>
                          <Badge variant="outline" className={`text-[10px] ${g.talonario === 'arca' ? 'border-indigo-400 text-indigo-700' : ''}`}>
                            {g.talonario.toUpperCase()}
                          </Badge>
                          <span className="font-medium text-sm">{g.target_label}</span>
                          {g.affiliate_type && (
                            <Badge className="text-[10px] bg-teal-100 text-teal-800" data-testid={`mb-affiliate-${g.group_key}`}>
                              {g.affiliate_type} · {g.affiliate_count} {g.affiliate_count === 1 ? 'afiliado' : 'afiliados'}
                            </Badge>
                          )}
                          {g.consolidada && <Badge className="text-[10px] bg-indigo-100 text-indigo-800">Agrupada</Badge>}
                          {g.fallback_to_fc && <Badge className="text-[10px] bg-amber-100 text-amber-800">A2 · sin origen → Factura</Badge>}
                        </div>
                        <span className="text-sm font-mono text-emerald-700">{fmt(g.total)}</span>
                      </div>
                      {g.nd_source && (
                        <p className="text-[11px] text-muted-foreground mb-1" data-testid={`mb-source-${g.group_key}`}>
                          Asociada a <b className="font-mono">{g.nd_source.invoice_number}</b>
                          {g.nd_source.from_run ? ' (se emite en esta misma corrida)' : ''}
                        </p>
                      )}
                      {(g.print_lines || []).length > 0 && (
                        <div className="rounded-md border bg-slate-50 dark:bg-slate-900/40 p-2 mt-1 mb-2"
                             data-testid={`mb-print-${g.group_key}`}>
                          <div className="text-[10px] uppercase text-muted-foreground mb-1">
                            Así se imprime en el formulario de la factura
                            {g.line_mode === 'summary' ? ' · resumido' : ' · un renglón por afiliado'}
                          </div>
                          {g.print_lines.map((pl, i) => (
                            <div key={i} className="flex justify-between gap-3 text-xs py-0.5">
                              <span>{pl.description}</span>
                              <span className="whitespace-nowrap font-mono">
                                {pl.quantity} × {fmt(pl.unit_price)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Residente</TableHead>
                          <TableHead>Concepto</TableHead>
                          <TableHead className="text-right">Cant.</TableHead>
                          <TableHead className="text-right">Precio</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {g.lines.map((ln, i) => (
                            <TableRow key={i} className={selections[lineKey(g.group_key, i)] ? '' : 'opacity-50'}>
                              <TableCell>
                                <input type="checkbox" checked={!!selections[lineKey(g.group_key, i)]}
                                       onChange={() => toggleLine(g.group_key, i)}
                                       data-testid={`mb-line-toggle-${g.group_key}-${i}`} />
                              </TableCell>
                              <TableCell className="text-xs">{ln.resident_name}</TableCell>
                              <TableCell className="text-xs">
                                {ln.description}
                                {ln.prorate_factor < 1.0 && (
                                  <Badge variant="outline" className="ml-2 text-[10px] border-amber-300 text-amber-700">Prorrateo {Math.round(ln.prorate_factor * 100)}%</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right text-xs">{ln.quantity}</TableCell>
                              <TableCell className="text-right text-xs">{fmt(ln.unit_price)}</TableCell>
                              <TableCell className="text-right text-xs font-medium">{fmt(ln.total)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                );
              })}
              {draftData.drafts.length === 0 && (
                <Card><CardContent className="pt-6 pb-6 text-center text-muted-foreground">
                  No hay comprobantes para emitir en este período. Revisá el informe de control.
                </CardContent></Card>
              )}
            </div>

            {draftData.control.warnings.length > 0 && (
              <div className="space-y-2" data-testid="mb-warnings">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Advertencias — la corrida sigue</p>
                {draftData.control.warnings.map(b => <ControlBlock key={b.code} block={b} kind="A" />)}
              </div>
            )}

            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="outline" onClick={() => { setStep(1); setDraftData(null); }} data-testid="mb-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" />Volver
              </Button>
              <Button onClick={execute} disabled={loading || blocked || summary.groups === 0}
                      className="bg-indigo-600 hover:bg-indigo-700" data-testid="mb-execute-btn">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {loading
                  ? `Emitiendo ${summary.groups} comprobante(s)…`
                  : `Emitir ${summary.groups} comprobante${summary.groups === 1 ? '' : 's'} (${fmt(summary.total)})`}
              </Button>
            </DialogFooter>
            {loading && summary.arca > 0 && (
              <p className="text-[11px] text-muted-foreground text-right" data-testid="mb-arca-progress">
                En ARCA el CAE se solicita de a un comprobante: puede demorar unos segundos por cada uno.
              </p>
            )}
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-3">
            <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-4 flex items-start gap-3" data-testid="mb-result-banner">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-medium text-emerald-800 dark:text-emerald-200">Corrida finalizada</p>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  Se emitieron <b>{result.created.length}</b> comprobantes por un total de <b>{fmt(result.total)}</b>.
                  {result.skipped?.length > 0 && <> {result.skipped.length} renglones se saltearon por duplicados (R2).</>}
                </p>
                <p className="text-[11px] text-emerald-700 dark:text-emerald-300 font-mono">ID corrida: {result.billing_run_id}</p>
              </div>
            </div>

            {result.rejected?.length > 0 && (
              <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-900 p-3" data-testid="mb-rejected">
                <div className="flex items-center gap-2 font-semibold text-sm text-rose-800 dark:text-rose-200">
                  <XCircle className="w-4 h-4" />
                  <span className="font-mono">R1</span> · {result.rejected.length} comprobante(s) rechazados por ARCA
                </div>
                <p className="text-xs text-rose-800/80 dark:text-rose-200/80 mt-1">
                  No se emitieron. Corregí el dato que indica ARCA y volvé a correr la facturación masiva: los ya
                  emitidos quedan fuera por el control anti-duplicado.
                </p>
                <Table className="mt-2">
                  <TableHeader><TableRow>
                    <TableHead className="text-xs">Destinatario</TableHead>
                    <TableHead className="text-xs">Comprobante</TableHead>
                    <TableHead className="text-xs text-right">Total</TableHead>
                    <TableHead className="text-xs">Error de ARCA</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {result.rejected.map((r, i) => (
                      <TableRow key={i} data-testid={`mb-rejected-${i}`}>
                        <TableCell className="text-xs">{r.target_label}</TableCell>
                        <TableCell className="text-xs">{r.doc_type === 'ND' ? 'Nota de débito' : 'Factura'} · {(r.talonario || '').toUpperCase()}</TableCell>
                        <TableCell className="text-xs text-right">{fmt(r.total)}</TableCell>
                        <TableCell className="text-xs text-rose-800 dark:text-rose-200">{r.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {result.notices?.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs text-amber-900 dark:text-amber-200" data-testid="mb-notices">
                {result.notices.map((n, i) => (
                  <div key={i}><span className="font-mono font-bold">{n.code}</span> · {n.target_label}: {n.detail}</div>
                ))}
              </div>
            )}

            <div className="flex gap-2 flex-wrap" data-testid="mb-summary-downloads">
              <Button variant="outline" size="sm" onClick={() => downloadRun('pdf')} data-testid="mb-download-pdf">
                <FileText className="w-4 h-4 mr-1.5" />Descargar PDF resumen
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadRun('xlsx')} data-testid="mb-download-xlsx">
                <FileSpreadsheet className="w-4 h-4 mr-1.5" />Descargar Excel resumen
              </Button>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Comprobante</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Destinatario</TableHead>
                <TableHead>CAE / Origen</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {result.created.map((c) => (
                  <TableRow key={c.invoice_id} data-testid={`mb-created-${c.invoice_id}`}>
                    <TableCell className="font-mono text-xs">{c.invoice_number}</TableCell>
                    <TableCell className="text-xs">
                      <Badge className={`text-[10px] ${c.document_type === 'ND' ? 'bg-violet-100 text-violet-800' : 'bg-blue-100 text-blue-800'}`}>
                        {c.document_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{c.target_label}</TableCell>
                    <TableCell className="text-[11px] font-mono">
                      {c.cae ? `CAE ${c.cae}` : '—'}
                      {c.nc_source_number ? ` · s/ ${c.nc_source_number}` : ''}
                    </TableCell>
                    <TableCell className="text-right font-medium">{fmt(c.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <DialogFooter>
              <Button onClick={() => onClose(false)} data-testid="mb-close-btn">Cerrar</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MassBillingDialog;
